import log from 'loglevel';
import React, { useState, useEffect, useRef } from 'react';
import ControlButton from '../ui/ControlButton';
import { usePlayerContext } from '../Context';
import { Events, Orientation, ShortcutAction } from '../../types/player';
import Shortcuts from '../../utils/Shortcut';

const Volume = () => {
  const { sdk } = usePlayerContext();
  const [muted, setMuted] = useState(sdk.isMuted());
  const [volume, setVolume] = useState(sdk.getVolume());

  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const isTouch = useRef(false);

  const settingsVolume = sdk.options().getControls().getSettings().volume;

  useEffect(() => {
    sdk.on(Events.VOLUME_CHANGED, (volume: number) => {
      setVolume(volume);
    });

    sdk.on(Events.MUTED, (muted: boolean) => {
      setMuted(muted);
    });

    sdk.on(Events.PIP_CHANGED, (inPip: boolean) => {
      if (!inPip) {
        if (sdk.isMuted() && !muted) {
          setMuted(muted);
        }
      }
    });
  }, []);

  const changeVolume = (newVolume: number) => {
    const volume = Math.max(0, Math.min(1, newVolume));
    if (volume === undefined || isNaN(volume)) return;
    sdk.setVolume(volume);
    setVolume(volume);
  };

  const toggleMute = () => {
    sdk.toggleMute();
  };

  let short = sdk.options().getShortCut(ShortcutAction.MUTE);
  if (short) {
    Shortcuts([
      {
        key: short.key ?? '',
        action: toggleMute,
        altKey: short.altKey,
        shiftKey: short.shiftKey,
        ctrlKey: short.ctrlKey,
      },
    ]);
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    const slider = sliderRef.current;
    let newVolume;
    if (slider) {
      const onMouseMove = (e: MouseEvent) => {
        handleAction(e.clientX, e.clientY);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isTouch.current) {
      isTouch.current = false;
      return;
    }
    handleAction(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const onTouchMove = (evt: TouchEvent) => {
      const touch = evt.touches[0];
      if (!touch) return;
      handleAction(touch.clientX, touch.clientY);
    };

    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    isTouch.current = true;
    const touch = e.changedTouches[0];
    handleAction(touch.clientX, touch.clientY);
  };

  const handleAction = (clientX: number, clientY: number) => {
    const slider = sliderRef.current;
    let newVolume;
    if (slider) {
      const rect = slider.getBoundingClientRect();
      if (
        settingsVolume &&
        settingsVolume.orientation === Orientation.PORTRAIT
      ) {
        newVolume = 1 - (clientY - rect.top) / rect.height;
        log.debug('newVolume', newVolume);
      } else {
        newVolume = (clientX - rect.left) / rect.width;
      }
      newVolume = Math.max(0, Math.min(1, newVolume));
      log.debug('newVolume by click', newVolume);
      changeVolume(newVolume);
    }
  };

  const prefix = sdk.getClassNamePrefix();

  if(sdk.options().hasAudio() === false)
    return null;

  return (
    <div
      className={`${prefix}-player-control ${prefix}-player-control-volume ${muted ? `${prefix}-player-control-volume-muted` : ''}`}
    >
      <div className={`${prefix}-player-control-volume-mute`}>
        <ControlButton
          className={'volume-mute'}
          icon={muted ? 'volumeXmark' : 'volumeHigh'}
          handleAction={toggleMute}
          title={muted ? 'Unmute' : 'Mute'}
        />
      </div>
      {settingsVolume && settingsVolume.range === true && (
        <div
          className={`${prefix}-player-control-volume-slider-container  ${prefix}-player-control-volume-slider-${settingsVolume.orientation}`}
        >
          <div
            className={`${prefix}-player-control-volume-slider`}
            ref={sliderRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onClick={handleClick}
            style={{ '--value': volume * 100 } as React.CSSProperties}
          >
            <div
              ref={thumbRef}
              className={`${prefix}-player-control-volume-thumb`}
              style={{ '--value': volume * 100 } as React.CSSProperties}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Volume;
